import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  Chip,
  Link
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { contactsApi, branchesApi } from '../../services/api';
import { Branch } from '../../types/branch';
import { Contact } from '../../types/contact';

// تعريف نوع البيانات للاستيراد
interface ImportData {
  name: string;
  phone?: string;
  branchId?: string;
  email?: string;
  status?: 'active' | 'inactive';
  category?: 'regular' | 'premium' | 'vip';
  errors?: string[];
  isValid?: boolean;
}

const ContactImport: React.FC = () => {
  const [importType, setImportType] = useState<'customer' | 'supplier'>('customer');
  const [file, setFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<ImportData[]>([]);
  const [previewData, setPreviewData] = useState<ImportData[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [importSuccess, setImportSuccess] = useState<boolean | null>(null);
  const [importStats, setImportStats] = useState({ total: 0, success: 0, failed: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // خطوات الاستيراد
  const steps = [
    'اختيار نوع الاستيراد',
    'تحميل الملف',
    'التحقق من البيانات',
    'استيراد البيانات'
  ];

  // جلب قائمة الفروع
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await branchesApi.getBranches({ status: 'active' });
        setBranches(response.branches);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchBranches();
  }, []);

  // تحميل الملف
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);
      parseExcelFile(selectedFile);
    }
  };

  // تحليل ملف الإكسل
  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);
        
        // تحويل البيانات إلى التنسيق المطلوب
        const formattedData: ImportData[] = jsonData.map((row: any) => ({
          name: row['الاسم'] || '',
          phone: row['رقم الهاتف'] || '',
          branchId: row['معرف الفرع'] || '',
          email: row['البريد الإلكتروني'] || '',
          status: row['الحالة'] === 'نشط' ? 'active' : 'inactive',
          category: getCategoryValue(row['الفئة']),
          errors: [],
          isValid: true
        }));

        setImportData(formattedData);
        validateData(formattedData);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // تحويل قيمة الفئة إلى القيمة المستخدمة في النظام
  const getCategoryValue = (categoryText: string): 'regular' | 'premium' | 'vip' => {
    if (categoryText === 'مميز') return 'premium';
    if (categoryText === 'VIP' || categoryText === 'vip') return 'vip';
    return 'regular'; // القيمة الافتراضية
  };

  // التحقق من صحة البيانات
  const validateData = (data: ImportData[]) => {
    const errors: string[] = [];
    const validatedData = data.map((item) => {
      const itemErrors: string[] = [];
      
      // التحقق من الاسم
      if (!item.name) {
        itemErrors.push('الاسم مطلوب');
      }
      
      // التحقق من رقم الهاتف للعملاء
      if (importType === 'customer' && !item.phone) {
        itemErrors.push('رقم الهاتف مطلوب للعملاء');
      }
      
      // التحقق من صحة معرف الفرع للعملاء
      if (importType === 'customer' && item.branchId) {
        const branchExists = branches.some(branch => branch._id === item.branchId);
        if (!branchExists) {
          itemErrors.push('معرف الفرع غير صحيح');
        }
      }
      
      return {
        ...item,
        errors: itemErrors,
        isValid: itemErrors.length === 0
      };
    });
    
    // جمع كل الأخطاء
    if (validatedData.some(item => !item.isValid)) {
      errors.push('بعض البيانات تحتوي على أخطاء، يرجى مراجعتها قبل الاستمرار');
    }

    if (validatedData.length === 0) {
      errors.push('لم يتم العثور على بيانات في الملف');
    }
    
    setValidationErrors(errors);
    setImportData(validatedData);
    setPreviewData(validatedData.slice(0, 10)); // عرض أول 10 سجلات فقط في المعاينة
  };

  // إنشاء قالب Excel للتنزيل
  const generateExcelTemplate = () => {
    const workbook = XLSX.utils.book_new();
    let data: any[] = [];

    if (importType === 'customer') {
      // قالب العملاء
      data = [
        {
          'الاسم': 'مثال: شركة الأفق',
          'رقم الهاتف': 'مثال: 0555555555',
          'معرف الفرع': 'مثال: 5f8d2e9b3a4c2e1b8a7d6f5e',
          'البريد الإلكتروني': 'مثال: info@example.com',
          'الحالة': 'نشط',
          'الفئة': 'عادي'
        }
      ];
      
      // إضافة معلومات الفروع المتاحة كصف ثاني
      if (branches.length > 0) {
        data.push({
          'الاسم': 'الفروع المتاحة:',
          'رقم الهاتف': '',
          'معرف الفرع': branches.map(b => `${b.name}: ${b._id}`).join(', '),
          'البريد الإلكتروني': '',
          'الحالة': 'الخيارات: نشط، غير نشط',
          'الفئة': 'الخيارات: عادي، مميز، VIP'
        });
      }
    } else {
      // قالب الموردين
      data = [
        {
          'الاسم': 'مثال: شركة الإمداد',
          'البريد الإلكتروني': 'مثال: supply@example.com',
          'رقم الهاتف': 'مثال: 0555555555',
          'الحالة': 'نشط',
          'الفئة': 'عادي'
        }
      ];
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // تعديل عرض الأعمدة
    const columnWidths = [
      { wch: 20 }, // الاسم
      { wch: 15 }, // رقم الهاتف
      { wch: 30 }, // معرف الفرع
      { wch: 25 }, // البريد الإلكتروني
      { wch: 10 }, // الحالة
      { wch: 10 }  // الفئة
    ];
    
    worksheet['!cols'] = columnWidths;
    
    XLSX.utils.book_append_sheet(workbook, worksheet, importType === 'customer' ? 'قالب العملاء' : 'قالب الموردين');
    
    // تصدير الملف
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, importType === 'customer' ? 'customer_import_template.xlsx' : 'supplier_import_template.xlsx');
  };

  // استيراد البيانات
  const importContacts = async () => {
    setIsLoading(true);
    try {
      const validData = importData.filter(item => item.isValid);
      
      console.log(`Attempting to import ${validData.length} ${importType === 'customer' ? 'customers' : 'suppliers'}`);
      
      // تحويل البيانات إلى التنسيق المطلوب للاستيراد الجماعي
      const contactsToImport = validData.map(item => {
        const contactData: any = {
          type: importType,
          name: item.name,
          category: item.category || 'regular',
          status: item.status || 'active',
          notes: '', // ملاحظات فارغة
        };
        
        // إضافة الحقول الاختيارية فقط إذا كانت موجودة
        if (item.email && item.email.trim()) {
          contactData.email = item.email.trim();
        }
        
        if (item.phone) {
          contactData.phone = item.phone;
        }
        
        if (importType === 'customer' && item.branchId) {
          contactData.branches = [item.branchId];
        } else {
          contactData.branches = [];
        }
        
        return contactData;
      });
      
      // استخدام الدالة الجديدة للاستيراد الجماعي
      console.log(`Sending ${contactsToImport.length} contacts for bulk import`);
      const result = await contactsApi.importContacts(contactsToImport);
      console.log('Import result:', result);
      
      // تحديث إحصائيات الاستيراد
      setImportStats({
        total: result.total,
        success: result.success,
        failed: result.failed
      });
      
      // عرض النتيجة النهائية
      setImportSuccess(result.failed === 0);
      
      // إذا كان هناك أخطاء، سجلها في وحدة التحكم للتشخيص
      if (result.errors && result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }
      
      setActiveStep(3); // الانتقال إلى الخطوة الأخيرة
    } catch (error) {
      console.error('Error during import process:', error);
      
      // عرض معلومات إضافية عن الخطأ للتشخيص
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      
      setImportSuccess(false);
      setImportStats({
        total: importData.filter(item => item.isValid).length,
        success: 0,
        failed: importData.filter(item => item.isValid).length
      });
    } finally {
      setIsLoading(false);
    }
  };

  // التعامل مع تغيير نوع الاستيراد
  const handleImportTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImportType(event.target.value as 'customer' | 'supplier');
    // إعادة تعيين البيانات عند تغيير نوع الاستيراد
    setFile(null);
    setImportData([]);
    setPreviewData([]);
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // التنقل بين الخطوات
  const handleNext = () => {
    if (activeStep === 1 && importData.length > 0) {
      validateData(importData);
    }
    
    // في الخطوة 2، نستورد إذا كان هناك سجلات صحيحة (حتى لو كان هناك أخطاء)
    if (activeStep === 2) {
      const validRecords = importData.filter(item => item.isValid);
      if (validRecords.length > 0) {
        importContacts();
        return;
      }
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setImportData([]);
    setPreviewData([]);
    setValidationErrors([]);
    setImportSuccess(null);
    setImportStats({ total: 0, success: 0, failed: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // محتوى الخطوات
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
                اختر نوع جهات الاتصال للاستيراد
              </FormLabel>
              <RadioGroup
                value={importType}
                onChange={handleImportTypeChange}
                row
              >
                <FormControlLabel
                  value="customer"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: 'success.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: importType === 'customer' ? 'bold' : 'normal' }}>
                      العملاء
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="supplier"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: importType === 'supplier' ? 'bold' : 'normal' }}>
                      الموردين
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
            
            <Box sx={{ mt: 4, p: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                {importType === 'customer' ? 'متطلبات استيراد العملاء:' : 'متطلبات استيراد الموردين:'}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" component="div">
                    <Box component="ul" sx={{ pl: 2 }}>
                      {importType === 'customer' ? (
                        <>
                          <Box component="li" sx={{ mb: 1 }}>
                            <strong>الاسم</strong>: (مطلوب) - اسم العميل
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <strong>رقم الهاتف</strong>: (مطلوب) - رقم هاتف العميل
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <strong>معرف الفرع</strong>: (مطلوب) - معرف الفرع الذي ينتمي إليه العميل
                          </Box>
                          <Box component="li">
                            <strong>البريد الإلكتروني</strong>: (اختياري)
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box component="li" sx={{ mb: 1 }}>
                            <strong>الاسم</strong>: (مطلوب) - اسم المورد
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <strong>البريد الإلكتروني</strong>: (اختياري)
                          </Box>
                          <Box component="li">
                            <strong>رقم الهاتف</strong>: (اختياري)
                          </Box>
                        </>
                      )}
                    </Box>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={generateExcelTemplate}
                    sx={{ mb: 2 }}
                    fullWidth
                  >
                    تنزيل قالب Excel
                  </Button>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    يمكنك تنزيل قالب Excel الجاهز وملؤه بالبيانات المطلوبة ثم استيراده.
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                bgcolor: 'background.paper',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(33, 150, 243, 0.04)',
                },
              }}
            >
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              {file ? (
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Chip
                      icon={<CheckIcon />}
                      label={file.name}
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '1rem', py: 2.5, px: 1 }}
                    />
                    <IconButton color="error" onClick={() => {
                      setFile(null);
                      setImportData([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    تم تحميل الملف بنجاح. يحتوي على {importData.length} سجل.
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    تغيير الملف
                  </Button>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    اسحب ملف Excel هنا أو انقر للتحميل
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    يجب أن يكون الملف بتنسيق .xlsx أو .xls
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    اختيار ملف
                  </Button>
                </>
              )}
            </Box>
            
            {file && importData.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  معاينة البيانات:
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>الاسم</TableCell>
                        {importType === 'customer' && <TableCell>رقم الهاتف</TableCell>}
                        {importType === 'customer' && <TableCell>معرف الفرع</TableCell>}
                        <TableCell>البريد الإلكتروني</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importData.slice(0, 5).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          {importType === 'customer' && <TableCell>{row.phone}</TableCell>}
                          {importType === 'customer' && <TableCell>{row.branchId}</TableCell>}
                          <TableCell>{row.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {importData.length > 5 && (
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                    تم عرض 5 سجلات فقط من إجمالي {importData.length} سجل.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 3 }}>
            {validationErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  يرجى تصحيح الأخطاء التالية قبل الاستمرار:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </Box>
              </Alert>
            )}
            
            <Typography variant="h6" gutterBottom>
              تحقق من البيانات قبل الاستيراد:
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'background.neutral' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      ملخص الاستيراد:
                    </Typography>
                    <Typography variant="body2">
                      <strong>نوع الاستيراد:</strong> {importType === 'customer' ? 'العملاء' : 'الموردين'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>عدد السجلات:</strong> {importData.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>السجلات الصحيحة:</strong> {importData.filter(item => item.isValid).length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>السجلات التي تحتوي على أخطاء:</strong> {importData.filter(item => !item.isValid).length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      ماذا يحدث بعد الاستيراد؟
                    </Typography>
                    <Typography variant="body2">
                      سيتم إضافة جهات الاتصال الجديدة إلى النظام مع الحالة المحددة.
                    </Typography>
                    <Typography variant="body2">
                      سيتم تجاهل السجلات التي تحتوي على أخطاء.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>الحالة</TableCell>
                      <TableCell>الاسم</TableCell>
                      {importType === 'customer' && <TableCell>رقم الهاتف</TableCell>}
                      {importType === 'customer' && <TableCell>معرف الفرع</TableCell>}
                      <TableCell>البريد الإلكتروني</TableCell>
                      <TableCell>الأخطاء</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importData.map((row, index) => (
                      <TableRow key={index} sx={{ bgcolor: row.isValid ? 'inherit' : 'rgba(255, 0, 0, 0.05)' }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {row.isValid ? (
                            <Chip
                              icon={<CheckIcon fontSize="small" />}
                              label="صحيح"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip
                              icon={<ErrorIcon fontSize="small" />}
                              label="خطأ"
                              size="small"
                              color="error"
                            />
                          )}
                        </TableCell>
                        <TableCell>{row.name}</TableCell>
                        {importType === 'customer' && <TableCell>{row.phone}</TableCell>}
                        {importType === 'customer' && (
                          <TableCell>
                            {row.branchId && branches.find(b => b._id === row.branchId)
                              ? `${branches.find(b => b._id === row.branchId)?.name} (${row.branchId})`
                              : row.branchId}
                          </TableCell>
                        )}
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          {row.errors && row.errors.length > 0 ? (
                            <Typography variant="body2" color="error">
                              {row.errors.join(', ')}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6">جاري استيراد البيانات...</Typography>
                <Typography variant="body2" color="text.secondary">
                  يرجى الانتظار حتى اكتمال العملية.
                </Typography>
              </Box>
            ) : importSuccess === true ? (
              <Box sx={{ my: 4 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'success.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, color: 'success.main' }}>
                  تم الاستيراد بنجاح!
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  تم استيراد {importStats.success} من أصل {importStats.total} سجل بنجاح.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                      <Typography variant="h4">{importStats.total}</Typography>
                      <Typography variant="body2">إجمالي السجلات</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <Typography variant="h4">{importStats.success}</Typography>
                      <Typography variant="body2">تم استيرادها بنجاح</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                      <Typography variant="h4">{importStats.failed}</Typography>
                      <Typography variant="body2">فشل استيرادها</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => window.location.href = '/contacts'}
                  >
                    الذهاب إلى قائمة جهات الاتصال
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                  >
                    استيراد ملف آخر
                  </Button>
                </Box>
              </Box>
            ) : importSuccess === false ? (
              <Box sx={{ my: 4 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'error.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, color: 'error.main' }}>
                  حدث خطأ أثناء الاستيراد
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {importStats.success > 0 
                    ? `تم استيراد ${importStats.success} سجل بنجاح و فشل استيراد ${importStats.failed} سجل.`
                    : 'لم يتم استيراد أي سجل. يرجى التحقق من البيانات والمحاولة مرة أخرى.'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReset}
                  sx={{ mx: 1 }}
                >
                  إعادة المحاولة
                </Button>
              </Box>
            ) : null}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, #eef2f6)', py: 4 }}>
      {/* العنوان */}
      <Box
        sx={{
          p: 3,
          mb: 4,
          bgcolor: 'white',
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(to right, #2196f3, #4caf50)'
          }
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: '#2c3e50',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              right: 0,
              width: 50,
              height: 3,
              bgcolor: 'primary.main',
              borderRadius: 1
            }
          }}
        >
          استيراد العملاء والموردين
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 2,
            mb: 2,
            maxWidth: 700
          }}
        >
          يمكنك استيراد بيانات العملاء والموردين من ملفات Excel. بالنسبة للعملاء، ستحتاج إلى الاسم ورقم الهاتف ومعرف الفرع. وبالنسبة للموردين، ستحتاج فقط إلى الاسم.
        </Typography>
        <Alert severity="info" sx={{ mt: 2, maxWidth: 700 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            📊 الحد الأقصى: 10,000 سجل في المرة الواحدة
          </Typography>
          <Typography variant="body2">
            ✅ النظام يستمر في الاستيراد حتى مع وجود أخطاء في بعض السجلات
          </Typography>
          <Typography variant="body2">
            ⚡ معالجة سريعة: ~1-2 دقيقة لكل 7000 سجل
          </Typography>
        </Alert>
      </Box>

      <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3 }}>
        <Card sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ pt: 4, pb: 3, px: 2 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Divider />
            <Box sx={{ p: 3 }}>
              {getStepContent(activeStep)}
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 3 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                السابق
              </Button>
              <Box>
                {activeStep !== steps.length - 1 && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={
                      (activeStep === 0 && !importType) ||
                      (activeStep === 1 && !file) ||
                      (activeStep === 2 && importData.filter(item => item.isValid).length === 0) ||
                      isLoading
                    }
                  >
                    {activeStep === steps.length - 2 ? 'استيراد' : 'التالي'}
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* معلومات مساعدة */}
        <Card sx={{ mb: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              نصائح للاستيراد
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon color="success" sx={{ mr: 1, mt: 0.5 }} />
                  <Typography variant="body2">
                    تأكد من استخدام القالب المتوفر للاستيراد لتجنب الأخطاء.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon color="success" sx={{ mr: 1, mt: 0.5 }} />
                  <Typography variant="body2">
                    تأكد من أن بيانات الفروع صحيحة وموجودة في النظام.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckIcon color="success" sx={{ mr: 1, mt: 0.5 }} />
                  <Typography variant="body2">
                    للعملاء، يجب توفير الاسم ورقم الهاتف ومعرف الفرع.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CheckIcon color="success" sx={{ mr: 1, mt: 0.5 }} />
                  <Typography variant="body2">
                    للموردين، يكفي توفير الاسم فقط.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ContactImport;